import {
  compact,
  difference,
  differenceBy,
  find,
  includes,
  isEqual,
  merge,
  reject,
  uniq,
  uniqBy,
} from "lodash/fp";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import {
  ReportSource,
  ReportView,
  ReportsPageDialog,
  TagCategory,
  Report,
} from "@app/analytics/MeetingReports/types";
import * as Events from "@app/analytics/tracking/events";
import type { Tag } from "@app/common/types";

type EditReport = { id: string; type: ReportView };

const TAG_CATEGORIES: TagCategory[] = ["auto", "custom"];

type Dialog = { id: ReportsPageDialog; reportId: Report["id"] };

interface ReportsTableStateValues {
  dialog: Dialog | null;
  editReport: {
    id: string;
    type: ReportView;
  } | null;
  groupRecurring: boolean;
  reportViews: ReportView[];
  sources: {
    all: ReportSource[];
    selected: ReportSource[];
  };
  tags: {
    selectedTags: Tag[];
    selectedCategories: TagCategory[];
    all: Tag[];
    byCategory: { [key in TagCategory]: Tag[] };
  };
}

interface ReportsTableState extends ReportsTableStateValues {
  closeDialog: () => void;
  initializeSources: (
    initialState: Partial<ReportsTableStateValues["sources"]>
  ) => void;
  initializeTags: (
    initialState: Partial<ReportsTableStateValues["tags"]>
  ) => void;
  setDialog: (dialog: Dialog | null) => void;
  setEditReport: (editReport: EditReport | null) => void;
  setGroupRecurring: (groupRecurring: boolean) => void;
  toggleReportView: (view: ReportView) => void;
  toggleSource: (source: ReportSource | "all") => void;
  toggleTag: (id: Tag["id"]) => void;
}

const DEFAULT_STATE: ReportsTableStateValues = {
  dialog: null,
  editReport: null,
  groupRecurring: false,
  reportViews: ["session", "topic"],
  sources: {
    all: [],
    selected: [],
  },
  tags: {
    selectedTags: [],
    selectedCategories: [],
    all: [],
    byCategory: {
      auto: [],
      custom: [],
    },
  },
};

const isTagCategory = (id: string): id is TagCategory =>
  includes(id, TAG_CATEGORIES);

const useReportsTable = createWithEqualityFn<ReportsTableState>(
  (set) => ({
    ...DEFAULT_STATE,
    closeDialog: () => set({ dialog: null }),
    setDialog: (dialog) => set({ dialog }),
    setEditReport: (editReport) => set({ editReport }),
    setGroupRecurring: (groupRecurring) => {
      Events.changeMeetingListView({ view: "group_recurring" });
      set({ groupRecurring });
    },
    initializeSources: (sources) =>
      set((state) => ({
        sources: merge({ ...state.sources }, sources),
      })),
    initializeTags: (tagState) =>
      set((state) => ({
        tags: merge({ ...state.tags }, tagState),
      })),
    toggleReportView: (reportView) =>
      set((state) => {
        const updatedReportViews = state.reportViews.includes(reportView)
          ? reject(isEqual(reportView), state.reportViews)
          : uniq([...state.reportViews, reportView]);
        return { reportViews: updatedReportViews };
      }),
    toggleSource: (source) =>
      set((state) => {
        let selectedSources = state.sources.selected;
        if (source === "all") {
          if (selectedSources.length < state.sources.all.length) {
            selectedSources = state.sources.all;
          } else {
            selectedSources = [];
          }
        } else if (includes(source, selectedSources)) {
          selectedSources = reject(isEqual(source), selectedSources);
        } else {
          selectedSources = uniq([...selectedSources, source]);
        }

        return {
          sources: {
            ...state.sources,
            selected: selectedSources,
          },
        };
      }),
    toggleTag: (id) =>
      set((state) => {
        let selectedTags = state.tags.selectedTags;
        let selectedCategories = state.tags.selectedCategories;

        if (isTagCategory(id)) {
          const isCategorySelected = includes(
            id,
            state.tags.selectedCategories
          );
          const categoryTags = state.tags.byCategory[id];

          selectedTags = isCategorySelected
            ? differenceBy("id", selectedTags, categoryTags)
            : uniqBy("id", [...selectedTags, ...categoryTags]);

          selectedCategories = isCategorySelected
            ? difference(selectedCategories, [id])
            : uniq([...selectedCategories, id]);
        } else if (find({ id }, selectedTags)) {
          // single tag is being toggled; is currently selected
          selectedTags = reject({ id }, selectedTags);
        } else {
          // single tag is being toggled; is not currently selected
          const tag = find({ id }, state.tags.all);
          selectedTags = compact([...selectedTags, tag]);
        }

        return {
          tags: { ...state.tags, selectedTags, selectedCategories },
        };
      }),
  }),
  shallow
);

export default useReportsTable;
